/**
 * Quest board kiosk panel (foot: F — QUEST BOARD near a village+ settlement).
 * Lists the settlement's generated offers for the current (town, epoch) plus
 * the campaign notice, with ACCEPT per row and a TURN IN section for ready
 * quests from THIS town. Board definitions are DERIVED locally (deterministic
 * from town seed + epoch) for an instant render; the `op:"board"` browse to
 * the server confirms the offerable set (campaign filtered by completion) and
 * re-renders on the reply. Styled inline (self-contained, like the dialog).
 */
import {
  campaignQuest,
  settlementQuestBoard,
  type PlanetDef,
  type QuestBoardMsg,
  type QuestDef,
  type QuestInstance,
  type NpcDef,
  type RegionSettlement,
} from "@hyperlight/shared";
import type { QuestLog } from "../rpg/quests";

/** Client-side mirror of the server cap (server is authoritative). */
const MAX_ACTIVE = 6;

export class QuestBoardPanel {
  visible = false;
  private el: HTMLElement;
  private titleEl: HTMLElement;
  private offersEl: HTMLElement;
  private turninEl: HTMLElement;
  private noteEl: HTMLElement;

  private townSeed = "";
  /** Local board defs by id (board offers, computed on open). */
  private localBoard = new Map<string, QuestDef>();
  /** Server-confirmed offerable ids (null until the board reply lands). */
  private offeredIds: string[] | null = null;

  constructor(
    private quests: QuestLog,
    private handlers: {
      onIntent: (m: { op: "accept" | "turnin"; q: string; town: string }) => void;
      onBrowse: (townSeed: string) => void;
      onClose: () => void;
    },
  ) {
    this.el = document.createElement("div");
    this.el.id = "quest-board";
    this.el.style.cssText = [
      "position:fixed",
      "left:50%",
      "top:50%",
      "transform:translate(-50%,-50%)",
      "width:600px",
      "max-width:92vw",
      "max-height:82vh",
      "box-sizing:border-box",
      "background:rgba(12,16,24,0.95)",
      "border:1px solid rgba(200,170,120,0.4)",
      "border-radius:6px",
      "padding:16px 18px",
      "color:#f0e6d2",
      "font-family:monospace",
      "display:none",
      "flex-direction:column",
      "z-index:42",
    ].join(";");
    this.el.innerHTML = `
      <div style="display:flex;justify-content:space-between;align-items:baseline;margin-bottom:4px">
        <b class="qb-title" style="font-size:16px;letter-spacing:2px;color:#ffd9a0"></b>
        <span style="opacity:0.5;font-size:11px">ESC — close</span>
      </div>
      <div class="qb-note" style="font-size:11px;opacity:0.6;margin-bottom:10px">QUEST BOARD</div>
      <div style="overflow-y:auto;flex:1;min-height:0;display:flex;flex-direction:column;gap:10px">
        <div class="qb-turnin" style="display:flex;flex-direction:column;gap:8px"></div>
        <div class="qb-offers" style="display:flex;flex-direction:column;gap:10px"></div>
      </div>`;
    document.body.appendChild(this.el);
    this.titleEl = this.el.querySelector(".qb-title")!;
    this.noteEl = this.el.querySelector(".qb-note")!;
    this.offersEl = this.el.querySelector(".qb-offers")!;
    this.turninEl = this.el.querySelector(".qb-turnin")!;

    const onClickList = (e: Event): void => {
      const btn = (e.target as HTMLElement).closest("button[data-act]") as HTMLElement | null;
      if (!btn || btn.hasAttribute("disabled")) return;
      const q = btn.dataset.q!;
      const op = btn.dataset.act as "accept" | "turnin";
      this.handlers.onIntent({ op, q, town: this.townSeed });
    };
    this.offersEl.addEventListener("click", onClickList);
    this.turninEl.addEventListener("click", onClickList);
  }

  /** Open at a settlement's board; kicks off a server browse for confirmation. */
  open(settlement: RegionSettlement, planet: PlanetDef, roster: readonly NpcDef[], epoch: number): void {
    this.townSeed = settlement.id;
    this.offeredIds = null;
    this.localBoard.clear();
    for (const def of settlementQuestBoard(settlement, planet, roster, epoch)) this.localBoard.set(def.id, def);
    this.titleEl.textContent = `${settlement.name.toUpperCase()} — QUEST BOARD`;
    this.noteEl.textContent = "ACCEPT A CONTRACT · TURN IN COMPLETED WORK";
    this.visible = true;
    this.el.style.display = "flex";
    this.render();
    this.handlers.onBrowse(this.townSeed);
  }

  close(): void {
    if (!this.visible) return;
    this.visible = false;
    this.el.style.display = "none";
    this.handlers.onClose();
  }

  /** Server board reply — adopt its offerable id set (and epoch: the ids are
   *  self-describing, so this stays correct across an epoch boundary skew). */
  onBoard(msg: QuestBoardMsg): void {
    if (!this.visible || msg.town !== this.townSeed) return;
    this.offeredIds = msg.q;
    this.noteEl.textContent = "ACCEPT A CONTRACT · TURN IN COMPLETED WORK";
    this.render();
  }

  /** Inventory/quest state changed while open — keep ACCEPT/TURN IN live. */
  refresh(): void {
    if (this.visible) this.render();
  }

  /** Resolve an offered id to its def (board local map, then campaign). */
  private defFor(id: string): QuestDef | null {
    return this.localBoard.get(id) ?? campaignQuest(id) ?? this.quests.resolveDef(id);
  }

  private render(): void {
    // Offer ids: the server's confirmed set once it lands, else the local
    // provisional set (board offers + the uncompleted campaign notice).
    let ids: string[];
    if (this.offeredIds) {
      ids = this.offeredIds;
    } else {
      ids = [...this.localBoard.keys()];
      const camp = campaignQuest("camp:first-steps");
      if (camp && !this.quests.isDone(camp.id)) ids = [camp.id, ...ids];
    }

    const atCap = this.quests.act.length >= MAX_ACTIVE;
    this.offersEl.innerHTML =
      ids.length === 0
        ? `<div style="opacity:0.5;font-size:12px;padding:12px 4px;text-align:center">The board is empty right now.</div>`
        : ids.map((id) => this.offerCard(id, atCap)).join("");

    // TURN IN: ready TOWN quests anchored to THIS settlement (campaign turns
    // in at its giver via dialog, not here).
    const ready = this.quests.act.filter(
      (i) =>
        this.quests.ready(i) &&
        this.quests.defOf(i)?.category === "town" &&
        this.quests.questTownSeed(i) === this.townSeed,
    );
    this.turninEl.innerHTML = ready.map((i) => this.turninCard(i)).join("");
  }

  private cardStyle(): string {
    return "border:1px solid rgba(180,150,110,0.3);border-radius:5px;padding:10px 12px;background:rgba(28,24,18,0.55)";
  }

  private btnStyle(enabled: boolean): string {
    return (
      "background:" +
      (enabled ? "#3a2f1c" : "#241f18") +
      ";border:1px solid " +
      (enabled ? "#caa46a" : "#4a4034") +
      ";color:" +
      (enabled ? "#ffd9a0" : "#7a715f") +
      ";font-family:monospace;padding:6px 16px;letter-spacing:1px;font-size:12px;border-radius:3px;cursor:" +
      (enabled ? "pointer" : "default")
    );
  }

  private offerCard(id: string, atCap: boolean): string {
    const def = this.defFor(id);
    if (!def) return "";
    const active = this.quests.hasActive(id);
    const done = this.quests.isDone(id);
    const objSummary = (def.stages[0]?.objectives ?? []).map((o) => o.text).join(" · ");
    const stagesNote = def.stages.length > 1 ? ` · ${def.stages.length}-STAGE` : "";
    const cat = def.category === "campaign" ? "CAMPAIGN" : "CONTRACT";
    const reward = this.quests.rewardLine(def);
    const enabled = !active && !done && !atCap;
    const label = done ? "COMPLETED" : active ? "ACCEPTED" : atCap ? "LOG FULL" : "ACCEPT";
    return `<div style="${this.cardStyle()}">
      <div style="display:flex;justify-content:space-between;align-items:baseline;gap:10px">
        <b style="font-size:13px;letter-spacing:1px;color:${def.category === "campaign" ? "#ffe08a" : "#f0e6d2"}">${def.title.toUpperCase()}</b>
        <span style="opacity:0.45;font-size:10px;letter-spacing:1px">${cat}${stagesNote}</span>
      </div>
      <div style="font-size:11px;opacity:0.7;margin:5px 0;font-style:italic">${def.blurb}</div>
      <div style="font-size:12px">${objSummary}</div>
      ${reward ? `<div style="font-size:11px;opacity:0.6;margin-top:5px">REWARD · ${reward}</div>` : ""}
      <div style="margin-top:9px">
        <button data-act="accept" data-q="${id}" ${enabled ? "" : "disabled"} style="${this.btnStyle(enabled)}">${label}</button>
      </div>
    </div>`;
  }

  private turninCard(inst: QuestInstance): string {
    const def = this.quests.defOf(inst);
    const title = def?.title ?? "QUEST";
    const reward = def ? this.quests.rewardLine(def) : "";
    return `<div style="${this.cardStyle()};border-color:#8fe08f55;background:rgba(24,40,26,0.5)">
      <div style="display:flex;justify-content:space-between;align-items:center;gap:10px">
        <div>
          <b style="font-size:13px;letter-spacing:1px;color:#bfe8a0">${title.toUpperCase()}</b>
          <div style="font-size:11px;opacity:0.7;margin-top:3px">Complete${reward ? ` · ${reward}` : ""}</div>
        </div>
        <button data-act="turnin" data-q="${inst.id}" style="${this.btnStyle(true)};border-color:#8fe08f;color:#bfe8a0">TURN IN</button>
      </div>
    </div>`;
  }
}
